import React, { useCallback, useEffect, useState } from "react";
import { Box, Typography, Card, Container, useTheme } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  selectAddeparInventory,
  selectAddeparInventoryLoading,
  selectAddeparInventoryLoaded,
  setAddeparInventory,
  setAddeparInventoryError,
  setAddeparInventoryLoading,
  updateAddeparInventoryItem,
} from "../../Redux/Reducers/addeparInventorySlice";
import {
  addActiveRequests,
  removeActiveRequests,
  updateLoading,
  updateLoadingMsg,
} from "../../Redux/Reducers/uiSlice";
import {
  fetchAddeparInventory,
  updateAddeparInventoryStatus,
} from "./addeparInventoryApi";
import { AddeparInventoryStatus } from "./addeparInventoryUtils";
import { addSMA, selectFunds } from "../../Redux/Reducers/fundsSlice";
import { updateManagerSMAs } from "../../Redux/Reducers/managersSlice";
import AddeparInventoryTable from "./AddeparInventoryTable";
import AddeparInventorySkeleton from "./Skeletons/AddeparInventorySkeleton";

const UPDATE_STATUS_REQUEST = "update_addepar_inventory_status";

const AddeparInventory = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const inventory = useSelector(selectAddeparInventory);
  const loading = useSelector(selectAddeparInventoryLoading);
  const loaded = useSelector(selectAddeparInventoryLoaded);
  const funds = useSelector(selectFunds);
  const [updatingId, setUpdatingId] = useState(null);

  const loadInventory = useCallback(async () => {
    if (loaded) return;
    dispatch(setAddeparInventoryLoading(true));
    const result = await fetchAddeparInventory();
    if (result.success) {
      dispatch(setAddeparInventory(result.data || []));
    } else {
      dispatch(setAddeparInventoryError(result.message || "Failed to load"));
      toast.error(result.message || "Failed to load Addepar inventory");
    }
  }, [dispatch, loaded]);

  useEffect(() => {
    loadInventory();
  }, [loadInventory]);

  const handleStatusUpdate = async (row, status, successLabel) => {
    const addeparInventoryId = row.addeparInventoryId || row.addeparEntityId;
    const loadingMessage =
      status === AddeparInventoryStatus.ONBOARDED
        ? "Adding to Vantage..."
        : "Ignoring inventory item...";

    setUpdatingId(addeparInventoryId);
    dispatch(addActiveRequests(UPDATE_STATUS_REQUEST));
    dispatch(updateLoading(true));
    dispatch(updateLoadingMsg(loadingMessage));

    try {
      const result = await updateAddeparInventoryStatus(
        addeparInventoryId,
        status
      );

      if (!result.success) {
        toast.error(result.message || `Failed to ${successLabel}`);
        return;
      }

      dispatch(
        updateAddeparInventoryItem(
          result.inventory || {
            addeparInventoryId,
            status,
          }
        )
      );

      if (status === AddeparInventoryStatus.ONBOARDED && result.sma) {
        dispatch(addSMA({ sma: result.sma }));
        dispatch(updateManagerSMAs({ sma: result.sma }));
      } else {
        toast.success(`${row.name || addeparInventoryId} ${successLabel}`);
      }
    } finally {
      setUpdatingId(null);
      dispatch(removeActiveRequests(UPDATE_STATUS_REQUEST));
      dispatch(updateLoading(false));
      dispatch(updateLoadingMsg(""));
    }
  };

  const handleIgnore = (row) =>
    handleStatusUpdate(row, AddeparInventoryStatus.IGNORED, "ignored");

  const handleImport = (row) => {
    const accountNumber = String(row?.accountNumber ?? "").trim();
    if (!accountNumber) {
      toast.error(
        "Cannot import: account number is required to onboard this inventory item"
      );
      return;
    }

    const name = String(row?.name || "").trim().toLowerCase();
    const abbrevFromAccount = accountNumber.toLowerCase();
    const existingSma = (funds || []).find((fund) => {
      if (!fund?.isSMA) return false;
      const fundName = String(fund?.name || "").trim().toLowerCase();
      const fundAbbrev = String(fund?.abbreviation || "").trim().toLowerCase();
      return (
        (name && fundName === name) ||
        (abbrevFromAccount && fundAbbrev === abbrevFromAccount)
      );
    });
    if (existingSma) {
      toast.error(
        `This SMA already exists (${row.name || accountNumber}). Cannot add to Vantage.`
      );
      return;
    }

    handleStatusUpdate(row, AddeparInventoryStatus.ONBOARDED, "imported");
  };

  const showSkeleton = loading || !loaded;

  return (
    <>
      <Helmet>
        <title>Addepar Inventory | Vantage Alpha</title>
      </Helmet>
      {showSkeleton ? (
        <AddeparInventorySkeleton />
      ) : (
        <Box
          sx={{
            backgroundColor:
              theme.palette.mode === "dark" ? theme.palette.grey[50] : "#ebebeb",
            background:
              theme.palette.mode === "dark" ? theme.palette.grey[50] : "#ebebeb",
            minHeight: "100vh",
            py: 3,
          }}
        >
          <Container maxWidth="xl">
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  mb: 1,
                }}
              >
                Addepar Inventory
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.secondary,
                }}
              >
                Review Addepar entities and import or ignore them in Vantage
              </Typography>
            </Box>

            <Card
              sx={{
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <AddeparInventoryTable
                inventory={inventory}
                updatingId={updatingId}
                onIgnore={handleIgnore}
                onImport={handleImport}
              />
            </Card>
          </Container>
        </Box>
      )}
    </>
  );
};

export default AddeparInventory;
